/**
 * Editor для ref.loyalty_settings (Порція Роздріб.5b).
 * One-active per organization — backend дає 400 якщо створюємо другий active.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { retailLoyaltySettings } from '@/api/retailLoyaltySettings';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';
import { useFormDraft } from '@/shared/hooks/useFormDraft';

const PERMISSION = 'retail.loyalty_settings.manage';

interface FormState {
  organization_id: string;
  accrual_percent: string;
  max_writeoff_percent: string;
  bonus_to_currency_rate: string;
  min_purchase_for_accrual: string;
  is_active: boolean;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  accrual_percent: '3.00',
  max_writeoff_percent: '50.00',
  bonus_to_currency_rate: '1.0000',
  min_purchase_for_accrual: '0',
  is_active: true,
  comment: '',
};

export default function LoyaltySettingsEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg(PERMISSION);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const docQuery = useQuery({
    queryKey: ['retail-loyalty-setting', id],
    queryFn: () => retailLoyaltySettings.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        accrual_percent: d.accrual_percent,
        max_writeoff_percent: d.max_writeoff_percent,
        bonus_to_currency_rate: d.bonus_to_currency_rate,
        min_purchase_for_accrual: d.min_purchase_for_accrual,
        is_active: d.is_active,
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const payload = () => ({
    accrual_percent: form.accrual_percent,
    max_writeoff_percent: form.max_writeoff_percent,
    bonus_to_currency_rate: form.bonus_to_currency_rate,
    min_purchase_for_accrual: form.min_purchase_for_accrual,
    is_active: form.is_active,
    comment: form.comment || null,
  });

  const create = useMutation({
    mutationFn: () =>
      retailLoyaltySettings.create({
        organization_id: form.organization_id,
        ...payload(),
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-loyalty-settings'] });
      navigate(`/retail/loyalty-settings/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () => retailLoyaltySettings.update(id!, payload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-loyalty-settings'] });
      qc.invalidateQueries({ queryKey: ['retail-loyalty-setting', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => retailLoyaltySettings.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-loyalty-settings'] });
      navigate('/retail/loyalty-settings');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const handleSave = () => {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
      },
      {
        organization_id: 'Юрособа',
      },
    );
    if (err) { setError(err); return; }
    if (isNew) create.mutate();
    else update.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова конфігурація лояльності' : 'Конфігурація лояльності'}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/retail/loyalty-settings')}>Скасувати</button>
          <RequirePermission code={PERMISSION}>
            <button
              className="primary"
              onClick={handleSave}
              disabled={create.isPending || update.isPending}
            >
              Зберегти
            </button>
          </RequirePermission>
          {!isNew && (
            <RequirePermission code={PERMISSION}>
              <button
                className="danger"
                onClick={() => {
                  if (window.confirm('Видалити конфігурацію?')) remove.mutate();
                }}
              >
                Видалити
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} — {o.code}
                </option>
              ))}
            </select>
          ) : (
            <div>
              {orgList.data?.items.find((o) => o.id === form.organization_id)?.name ??
                form.organization_id}
            </div>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>% нарахування</label>
          <input
            type="number"
            step="0.01"
            value={form.accrual_percent}
            onChange={(e) => setForm({ ...form, accrual_percent: e.target.value })}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Макс. % списання</label>
          <input
            type="number"
            step="0.01"
            value={form.max_writeoff_percent}
            onChange={(e) => setForm({ ...form, max_writeoff_percent: e.target.value })}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Курс Бонус→Грн</label>
          <input
            type="number"
            step="0.0001"
            value={form.bonus_to_currency_rate}
            onChange={(e) =>
              setForm({ ...form, bonus_to_currency_rate: e.target.value })
            }
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Мін. сума покупки для нарахування</label>
          <input
            type="number"
            step="0.01"
            value={form.min_purchase_for_accrual}
            onChange={(e) =>
              setForm({ ...form, min_purchase_for_accrual: e.target.value })
            }
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            {' '}Активна
          </label>
        </div>
        <div className="field" style={{ gridColumn: 'span 12' }}>
          <label>Коментар</label>
          <textarea
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: 'var(--badge-red-fg)' }}>
          {error}
        </div>
      )}
    </div>
  );
}
